import { useCallback, useEffect, useRef, useState } from "react";
import type { GpsFix } from "./types";

/**
 * Device position for proof capture.
 *
 * REQ-MKT-1 asks for "exact" GPS and REQ-DLV-2 for "precise" GPS, so this
 * hook does two things a naive getCurrentPosition call does not:
 *
 * 1. Watches rather than reads once. The first fix a phone returns is
 *    routinely a cell-tower estimate accurate to hundreds of metres; the GPS
 *    fix arrives seconds later. Watching and keeping the best fix means the
 *    agent standing at the customer's gate records the gate, not the
 *    neighbourhood.
 * 2. Reports accuracy, so the UI can refuse a fix that is too vague instead
 *    of silently recording a bad one.
 */

const GOOD_ACCURACY_METRES = 50;
const WATCH_TIMEOUT_MS = 20_000;

export type GeolocationState =
  | { status: "idle" }
  | { status: "locating"; best?: GpsFix }
  | { status: "ready"; fix: GpsFix }
  | { status: "error"; message: string; canRetry: boolean };

export function useGeolocation(enabled: boolean) {
  const [state, setState] = useState<GeolocationState>({ status: "idle" });
  const watchIdRef = useRef<number | null>(null);
  const bestRef = useRef<GpsFix | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setState({
        status: "error",
        message: "This browser cannot provide a location.",
        canRetry: false,
      });
      return;
    }

    bestRef.current = undefined;
    setState({ status: "locating" });

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const fix: GpsFix = {
          lat: position.coords.latitude,
          long: position.coords.longitude,
          accuracyMetres: Math.round(position.coords.accuracy),
          takenAt: new Date(position.timestamp).toISOString(),
        };

        // Keep the tightest fix seen so far.
        const best = bestRef.current;
        if (!best || fix.accuracyMetres < best.accuracyMetres) {
          bestRef.current = fix;
        }
        const current = bestRef.current!;

        if (current.accuracyMetres <= GOOD_ACCURACY_METRES) {
          stop();
          setState({ status: "ready", fix: current });
        } else {
          setState({ status: "locating", best: current });
        }
      },
      (error) => {
        stop();
        const denied = error.code === error.PERMISSION_DENIED;
        setState({
          status: "error",
          message: denied
            ? "Location permission was denied. Allow location access for this site in your browser settings, then try again."
            : "Could not get a location fix. Move somewhere with a clearer view of the sky and try again.",
          canRetry: true,
        });
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: WATCH_TIMEOUT_MS },
    );

    // Accept the best fix we have once the window closes, rather than
    // failing outright - a 120 m fix beats no proof at all, and the UI shows
    // the accuracy so it is an informed choice.
    timerRef.current = setTimeout(() => {
      stop();
      const best = bestRef.current;
      setState(
        best
          ? { status: "ready", fix: best }
          : {
              status: "error",
              message:
                "Timed out waiting for a location fix. Try again, ideally outdoors.",
              canRetry: true,
            },
      );
    }, WATCH_TIMEOUT_MS);
  }, [stop]);

  useEffect(() => {
    if (enabled) start();
    return stop;
  }, [enabled, start, stop]);

  return {
    state,
    retry: start,
    isPrecise:
      state.status === "ready" && state.fix.accuracyMetres <= GOOD_ACCURACY_METRES,
    goodAccuracyMetres: GOOD_ACCURACY_METRES,
  };
}
